import { useState } from "react";
import { Phone, MapPin, Handshake, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Overline } from "@/components/Bits";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const PARTNERSHIPS = [
  "Speaking Engagements", "Leadership Conferences", "Church Training", "Business Consulting",
  "Executive Coaching", "AI Academy Partnerships", "Media Interviews", "International Ministry Collaboration",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", interest: "", message: "" });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received. We'll be honoured to connect with you soon.");
      setForm({ name: "", email: "", phone: "", subject: "", interest: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        overline="Let's build the Kingdom together"
        title={<>We would be <span className="font-accent text-gold-gradient italic">honoured</span> to connect.</>}
        subtitle="Invite Apostle Blaise Judah to speak, request leadership training, seek business consulting, or partner with House of Blessing Ministries."
      />

      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 grid gap-16 lg:grid-cols-[1fr_1.2fr]">
        {/* Info */}
        <Reveal>
          <Overline>Contact Information</Overline>
          <p className="mt-6 font-serif text-3xl text-white">Apostle Blaise Judah</p>
          <p className="text-sm text-gold-400 uppercase tracking-[0.2em] mt-2">Founder & Senior Pastor</p>
          <p className="text-sm text-white/60 mt-1">House of Blessing Ministries</p>

          <div className="mt-10 space-y-6">
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 items-center justify-center border border-gold-500/30 text-gold-500 shrink-0"><Phone size={18} /></span>
              <div className="text-white/80">
                <p className="text-xs uppercase tracking-[0.2em] text-gold-500 mb-1">Telephone</p>
                <p>+237 699 451 241</p>
                <p>+237 653 468 440</p>
                <p>+237 672 358 930</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <a
                href="https://wa.me/237699451241?text=Hello%20Apostle%20Blaise%20Judah%20%E2%80%94%20I%20visited%20the%20Ministry%20website%20and%20would%20love%20to%20connect."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-link"
                className="flex items-start gap-4 group"
              >
                <span className="flex h-11 w-11 items-center justify-center bg-[#0b8a4f] text-white shrink-0 group-hover:bg-[#0a7a45] transition-colors">
                  <MessageCircle size={18} strokeWidth={1.8} />
                </span>
                <div className="text-white/80">
                  <p className="text-xs uppercase tracking-[0.2em] text-gold-500 mb-1">WhatsApp — instant chat</p>
                  <p className="group-hover:text-gold-300 transition-colors">+237 699 451 241 — tap to open WhatsApp</p>
                </div>
              </a>
            </div>
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 items-center justify-center border border-gold-500/30 text-gold-500 shrink-0"><Mail size={18} /></span>
              <div className="text-white/80">
                <p className="text-xs uppercase tracking-[0.2em] text-gold-500 mb-1">Email</p>
                <a href="mailto:info@apostleblaisejudah.com" className="hover:text-gold-400 transition-colors break-all">info@apostleblaisejudah.com</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 items-center justify-center border border-gold-500/30 text-gold-500 shrink-0"><MapPin size={18} /></span>
              <div className="text-white/80">
                <p className="text-xs uppercase tracking-[0.2em] text-gold-500 mb-1">Address</p>
                <p>P.O. Box 1, Tiko – Cameroon</p>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <div className="flex items-center gap-2 mb-5">
              <Handshake size={18} className="text-gold-500" />
              <Overline>Partnership Opportunities</Overline>
            </div>
            <div className="flex flex-wrap gap-2">
              {PARTNERSHIPS.map((p) => (
                <span key={p} className="border border-gold-500/25 px-3 py-1.5 text-xs text-white/70">{p}</span>
              ))}
            </div>
          </div>
        </Reveal>

        {/* Form */}
        <Reveal delay={0.1}>
          <form onSubmit={submit} className="glass corner-frame p-8 md:p-10 space-y-5" data-testid="contact-form">
            <div className="grid gap-5 sm:grid-cols-2">
              <Input required placeholder="Full name" value={form.name} onChange={set("name")} data-testid="contact-name" className="bg-ink border-gold-500/25 text-white placeholder:text-white/40" />
              <Input required type="email" placeholder="Email address" value={form.email} onChange={set("email")} data-testid="contact-email" className="bg-ink border-gold-500/25 text-white placeholder:text-white/40" />
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <Input placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} data-testid="contact-phone" className="bg-ink border-gold-500/25 text-white placeholder:text-white/40" />
              <select value={form.interest} onChange={set("interest")} data-testid="contact-interest" className="w-full bg-ink border border-gold-500/25 text-white/90 px-3 py-2.5 text-sm outline-none focus:border-gold-400">
                <option value="">I'm interested in…</option>
                {PARTNERSHIPS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <Input required placeholder="Subject" value={form.subject} onChange={set("subject")} data-testid="contact-subject" className="bg-ink border-gold-500/25 text-white placeholder:text-white/40" />
            <Textarea required rows={5} placeholder="Your message" value={form.message} onChange={set("message")} data-testid="contact-message" className="bg-ink border-gold-500/25 text-white placeholder:text-white/40 resize-none" />
            <Button type="submit" disabled={busy} data-testid="contact-submit" className="w-full bg-gold-500 text-ink hover:bg-gold-400 font-semibold py-6 text-sm">
              {busy ? "Sending…" : "Send Message"}
            </Button>
          </form>
        </Reveal>
      </section>
    </div>
  );
}
